import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export const generateInvoice = async (booking: any) => {
    const doc = new jsPDF();
    
    // Attempt to parse details
    let details: any = {};
    try {
        details = typeof booking.booking_details === 'string' ? JSON.parse(booking.booking_details) : (booking.booking_details || {});
    } catch {}

    const email = booking.contact_email || details.contact_email || details.email || 'N/A';
    const phone = booking.contact_phone || details.contact_phone || details.phone || 'N/A';
    
    // Add Logo (Async)
    try {
        const logoUrl = 'https://static.readdy.ai/image/3d693276a5424479ffa5f1cc868e7c58/99585d34dbb7c267061331244ebc5e82.png';
        const response = await fetch(logoUrl);
        const blob = await response.blob();
        const reader = new FileReader();
        await new Promise((resolve) => {
            reader.onloadend = resolve;
            reader.readAsDataURL(blob);
        });
        const base64data = reader.result as string;
        doc.addImage(base64data, 'PNG', 14, 15, 30, 15);
    } catch (e) {
        console.warn('Could not load logo for invoice', e);
        doc.setFontSize(22);
        doc.setTextColor(218, 165, 32); // Gold
        doc.setFont('helvetica', 'bold');
        doc.text('RIGLAND', 14, 25);
    }

    // Header styling
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(24);
    doc.setTextColor(40, 40, 40);
    doc.text('INVOICE', doc.internal.pageSize.width - 14, 25, { align: 'right' });

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Invoice Number: INV-${booking.id.slice(0, 8).toUpperCase()}`, doc.internal.pageSize.width - 14, 32, { align: 'right' });
    doc.text(`Date: ${new Date(booking.created_at).toLocaleDateString()}`, doc.internal.pageSize.width - 14, 37, { align: 'right' });
    doc.text(`Status: ${(booking.status || 'unknown').toUpperCase()}`, doc.internal.pageSize.width - 14, 42, { align: 'right' });

    // Divider Line
    doc.setDrawColor(218, 165, 32); // Gold line
    doc.setLineWidth(0.5);
    doc.line(14, 48, doc.internal.pageSize.width - 14, 48);

    // Company Info & Customer Info Side by Side
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 40, 40);
    doc.text('Rigland Travels Ltd', 14, 55);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    const companyAddress = [
        'Office No 12, Top Floor',
        'NNDC Mall Building No 6',
        'Muhammadu Buhari Way',
        'Kaduna, Nigeria.',
        'info@riglandtravels.com | +234 806 767 7144'
    ];
    doc.text(companyAddress, 14, 60);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 40, 40);
    doc.text('Bill To:', doc.internal.pageSize.width - 14, 55, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text(email, doc.internal.pageSize.width - 14, 60, { align: 'right' });
    doc.text(phone, doc.internal.pageSize.width - 14, 65, { align: 'right' });

    let currentY = 95;

    // Travelers Table
    const rawTravelers = booking.booking_travelers || details.passengers || [];
    if (Array.isArray(rawTravelers) && rawTravelers.length > 0) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(40, 40, 40);
        doc.text('Traveler Information', 14, currentY);
        
        const travelersData = rawTravelers.map((t: any) => [
            `${t.first_name || t.given_name || t.firstName || ''} ${t.last_name || t.family_name || t.lastName || ''}`.trim(),
            t.passport_number || t.passportNumber || 'N/A',
            t.nationality || 'N/A',
            t.dob || t.born_on || t.age || 'N/A',
            t.gender || 'N/A'
        ]);

        autoTable(doc, {
            startY: currentY + 5,
            head: [['Name', 'Passport', 'Nationality', 'Age/DOB', 'Gender']],
            body: travelersData,
            theme: 'grid',
            headStyles: { fillColor: [26, 32, 44], textColor: 255 }, // Slate-900
            styles: { fontSize: 9, cellPadding: 4 },
            alternateRowStyles: { fillColor: [248, 250, 252] }
        });
        // @ts-ignore
        currentY = doc.lastAutoTable.finalY + 15;
    }

    // Items Table
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 40, 40);
    doc.text('Booking Summary', 14, currentY);

    const formatCurrency = (val: number, cur: string) => {
        return cur === 'NGN' ? `NGN ${val.toLocaleString()}` : `$${val.toLocaleString()}`;
    };

    let itemsData = [];
    const rawItems = booking.booking_items || [];
    
    if (Array.isArray(rawItems) && rawItems.length > 0) {
        itemsData = rawItems.map((item: any) => {
            let itemDesc = JSON.stringify(item.details);
            if (item.service_type === 'hotel') itemDesc = `${item.details.destination || 'Hotel'} (${item.details.rooms || 1} Rooms)`;
            if (item.service_type === 'flight') itemDesc = `Flight Route: ${item.details.from} -> ${item.details.to}`;
            
            return [
                (item.service_type || '').toUpperCase(),
                itemDesc,
                formatCurrency(Number(item.price || 0), booking.currency || 'NGN')
            ];
        });
    } else {
        itemsData = [
            [(booking.service_type || 'General Booking').toUpperCase(), 'Total Package / Service Payload', formatCurrency(Number(booking.amount || booking.total_amount || 0), booking.currency || 'NGN')]
        ];
    }

    autoTable(doc, {
        startY: currentY + 5,
        head: [['Service', 'Description', 'Amount']],
        body: itemsData,
        theme: 'grid',
        headStyles: { fillColor: [218, 165, 32], textColor: 255 }, // Gold
        styles: { fontSize: 10, cellPadding: 5 },
        columnStyles: { 2: { halign: 'right' } },
        foot: [['', 'Grand Total', formatCurrency(Number(booking.amount || booking.total_amount || 0), booking.currency || 'NGN')]],
        footStyles: { fillColor: [26, 32, 44], textColor: 255, fontStyle: 'bold', halign: 'right' }
    });

    // Footer
    // @ts-ignore
    const finalY = doc.lastAutoTable.finalY + 20;
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.setFont('helvetica', 'italic');
    doc.text('Thank you for choosing Rigland Travels. Wishing you a safe and memorable journey!', doc.internal.pageSize.width / 2, finalY, { align: 'center' });

    doc.save(`Rigland_Invoice_${booking.id.slice(0, 8)}.pdf`);
};

export const generateETicket = async (booking: any) => {
    const doc = new jsPDF();
    
    // Attempt to parse details
    let details: any = {};
    try {
        details = typeof booking.booking_details === 'string' ? JSON.parse(booking.booking_details) : (booking.booking_details || {});
    } catch {}

    const pnr = details.pnr || details.letsfg_booking_id || booking.id.slice(0, 8).toUpperCase();
    
    // Add Logo (Async)
    try {
        const logoUrl = 'https://static.readdy.ai/image/3d693276a5424479ffa5f1cc868e7c58/99585d34dbb7c267061331244ebc5e82.png';
        const response = await fetch(logoUrl);
        const blob = await response.blob();
        const reader = new FileReader();
        await new Promise((resolve) => {
            reader.onloadend = resolve;
            reader.readAsDataURL(blob);
        });
        const base64data = reader.result as string;
        doc.addImage(base64data, 'PNG', 14, 15, 30, 15);
    } catch (e) {
        console.warn('Could not load logo for e-ticket', e);
        doc.setFontSize(22);
        doc.setTextColor(218, 165, 32);
        doc.setFont('helvetica', 'bold');
        doc.text('RIGLAND', 14, 25);
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(24);
    doc.setTextColor(26, 32, 44); // Slate-900
    doc.text('E-TICKET', doc.internal.pageSize.width - 14, 25, { align: 'right' });

    const orderNumber = booking.id.slice(0, 12).toUpperCase();
    const amountStr = booking.currency === 'NGN' ? `NGN ${Number(booking.amount || 0).toLocaleString()}` : `$${Number(booking.amount || 0).toLocaleString()}`;
    const paymentMethod = booking.payment_method || 'Paystack / Card';

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Booking Ref (PNR): ${pnr}`, doc.internal.pageSize.width - 14, 32, { align: 'right' });
    doc.text(`Order Number: ${orderNumber}`, doc.internal.pageSize.width - 14, 37, { align: 'right' });
    doc.text(`Date of Issue: ${new Date(booking.created_at).toLocaleDateString()}`, doc.internal.pageSize.width - 14, 42, { align: 'right' });
    doc.text(`Status: ${(booking.status || 'unknown').toUpperCase()}`, doc.internal.pageSize.width - 14, 47, { align: 'right' });

    // Divider Line
    doc.setDrawColor(218, 165, 32); // Gold line
    doc.setLineWidth(0.5);
    doc.line(14, 53, doc.internal.pageSize.width - 14, 53);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 40, 40);
    doc.text('Rigland Travels Ltd', 14, 60);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    const companyAddress = [
        'Office No 12, Top Floor',
        'NNDC Mall Building No 6',
        'Muhammadu Buhari Way',
        'Kaduna, Nigeria.',
        'info@riglandtravels.com | +234 806 767 7144'
    ];
    doc.text(companyAddress, 14, 65);

    // Payment Info & QR Code
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 40, 40);
    doc.text('Payment Details:', doc.internal.pageSize.width - 45, 60);
    doc.setFont('helvetica', 'normal');
    doc.text(`Amount Paid: ${amountStr}`, doc.internal.pageSize.width - 45, 65);
    doc.text(`Paid Via: ${paymentMethod}`, doc.internal.pageSize.width - 45, 70);

    // Add QR Barcode
    try {
        const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(pnr)}`;
        const qrResponse = await fetch(qrUrl);
        const qrBlob = await qrResponse.blob();
        const qrReader = new FileReader();
        await new Promise((resolve) => {
            qrReader.onloadend = resolve;
            qrReader.readAsDataURL(qrBlob);
        });
        const qrBase64 = qrReader.result as string;
        doc.addImage(qrBase64, 'PNG', doc.internal.pageSize.width - 35, 75, 20, 20);
    } catch (e) {
        console.warn('Could not load QR barcode', e);
    }

    let currentY = 100;

    // Travelers Table
    const rawTravelers = booking.booking_travelers || details.passengers || [];
    if (Array.isArray(rawTravelers) && rawTravelers.length > 0) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(40, 40, 40);
        doc.text('Passenger Information', 14, currentY);
        
        const travelersData = rawTravelers.map((t: any) => [
            `${t.first_name || t.given_name || t.firstName || ''} ${t.last_name || t.family_name || t.lastName || ''}`.trim(),
            t.passport_number || t.passportNumber || 'N/A',
            t.nationality || 'N/A'
        ]);

        autoTable(doc, {
            startY: currentY + 5,
            head: [['Passenger Name', 'Passport Number', 'Nationality']],
            body: travelersData,
            theme: 'grid',
            headStyles: { fillColor: [26, 32, 44], textColor: 255 },
            styles: { fontSize: 10, cellPadding: 5 }
        });
        // @ts-ignore
        currentY = doc.lastAutoTable.finalY + 15;
    }

    // Itinerary Table
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 40, 40);
    doc.text('Itinerary', 14, currentY);

    let itemsData = [];
    const rawItems = booking.booking_items || [];
    
    const airportMap: Record<string, string> = {
        'LOS': 'Lagos, Nigeria',
        'ABV': 'Abuja, Nigeria',
        'PHC': 'Port Harcourt, Nigeria',
        'KAN': 'Kano, Nigeria',
        'JED': 'Jeddah, Saudi Arabia',
        'MED': 'Medina, Saudi Arabia',
        'RUH': 'Riyadh, Saudi Arabia',
        'LHR': 'London Heathrow, UK',
        'DXB': 'Dubai, UAE',
        'JFK': 'New York, USA',
        'CDG': 'Paris, France',
        'CAI': 'Cairo, Egypt'
    };

    const getFullRoute = (fromCode: string, toCode: string) => {
        const fromUpper = (fromCode || '').toUpperCase();
        const toUpper = (toCode || '').toUpperCase();
        
        const fromName = airportMap[fromUpper] || fromCode || 'Unknown Origin';
        const toName = airportMap[toUpper] || toCode || 'Unknown Destination';
        
        return `${fromName} -> ${toName}`;
    };

    if (Array.isArray(rawItems) && rawItems.length > 0) {
        itemsData = rawItems.map((item: any) => {
            let itemDesc = JSON.stringify(item.details);
            if (item.service_type === 'hotel') itemDesc = `${item.details.destination || 'Hotel'} (${item.details.rooms || 1} Rooms) - Check In: ${item.details.checkIn || 'N/A'}`;
            if (item.service_type === 'flight') itemDesc = `Flight Route: ${getFullRoute(item.details.from || details.origin, item.details.to || details.destination)} | Airline: ${item.details.airline || 'N/A'}`;
            
            return [
                (item.service_type || '').toUpperCase(),
                itemDesc
            ];
        });
    } else {
        const fallbackRoute = getFullRoute(details.origin || details.from, details.destination || details.to);
        itemsData = [
            [(booking.service_type || 'General Booking').toUpperCase(), fallbackRoute !== 'Unknown Origin -> Unknown Destination' ? `Flight: ${fallbackRoute}` : 'Travel Package Service']
        ];
    }

    autoTable(doc, {
        startY: currentY + 5,
        head: [['Service Type', 'Details']],
        body: itemsData,
        theme: 'grid',
        headStyles: { fillColor: [218, 165, 32], textColor: 255 },
        styles: { fontSize: 10, cellPadding: 5 }
    });

    // Footer
    // @ts-ignore
    const finalY = doc.lastAutoTable.finalY + 25;
    
    // Check if we need a new page for footer
    if (finalY > doc.internal.pageSize.height - 30) {
        doc.addPage();
    }
    
    const trueFinalY = finalY > doc.internal.pageSize.height - 30 ? 30 : finalY;
    
    doc.setFontSize(11);
    doc.setTextColor(40, 40, 40);
    doc.setFont('helvetica', 'bold');
    doc.text('Important Information', 14, trueFinalY);
    
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    doc.text([
        '1. Please arrive at the airport at least 3 hours before departure for international flights.',
        '2. Ensure you have valid travel documents, including passport and necessary visas.',
        '3. This E-Ticket is valid for travel as specified above.'
    ], 14, trueFinalY + 6);

    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.setFont('helvetica', 'italic');
    doc.text('Thank you for choosing Rigland Travels. Have a safe journey!', doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 10, { align: 'center' });

    doc.save(`Rigland_ETicket_${pnr}.pdf`);
};
